import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useFormikContext } from 'formik';

const getMachineryOption = (optionTitle: string | number) => {
  if (typeof optionTitle === 'string' && optionTitle.includes('/')) {
    const titleArray = optionTitle.split('/');
    return (
      <Stack direction="row">
        <Typography sx={{ pr: 1 }}>{titleArray[0]}</Typography>/
        <Typography sx={{ opacity: 0.5, ml: 1 }}>{titleArray[1]}</Typography>
      </Stack>
    );
  }
  return false;
};

type Props = {
  name: string;
  label: string;
  hideHelperText?: boolean;
  helperText?: string;
  menuItems?:
    | { [key: string]: string }
    | { [key: string]: number }
    | string[]
    | number[];
  defaultMenuItems?: { value: string; text: string };
} & TextFieldProps;

export const Input = forwardRef<HTMLInputElement, Props>((props, ref) => {
  const {
    helperText,
    hideHelperText = false,
    menuItems,
    defaultMenuItems,
    name,
    label,
    ...rest
  } = props;
  const { t } = useTranslation();
  const { palette } = useTheme();
  const { values, errors, touched, handleChange, handleBlur } =
    useFormikContext<any>();
  const isError = !!(errors[name] && touched[name]);

  const getError = (key: string) =>
    !!errors[key] && touched[key] ? t(`validationList.${errors[key]}`) : '';

  return (
    <Box sx={{ width: '100%' }}>
      <TextField
        ref={ref}
        fullWidth
        name={name}
        label={t(label)}
        value={props.select && !values[name] ? -1 : values[name]}
        onChange={handleChange}
        onBlur={handleBlur}
        error={isError}
        {...rest}
        SelectProps={{
          style:
            values[name] < 1 || !values[name]
              ? { color: palette.text.disabled }
              : {},
        }}
      >
        {defaultMenuItems && (
          <MenuItem value={defaultMenuItems.value} sx={{ display: 'none' }}>
            {t(defaultMenuItems.text)}
          </MenuItem>
        )}
        {menuItems &&
          (Array.isArray(menuItems)
            ? menuItems.map((el, index) => (
                <MenuItem key={index} value={el}>
                  {typeof el === 'string' ? t(el) : el}
                </MenuItem>
              ))
            : Object.keys(menuItems).map((key, index) => (
                <MenuItem key={index} value={key}>
                  {getMachineryOption(menuItems[key])
                    ? getMachineryOption(menuItems[key])
                    : typeof menuItems[key] === 'string'
                    ? t(menuItems[key] as string)
                    : menuItems[key]}
                </MenuItem>
              )))}
      </TextField>
      {!hideHelperText && (
        <FormHelperText error>{getError(name)}</FormHelperText>
      )}
    </Box>
  );
});
